import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, ClipboardList, BarChart3, Send, CheckCircle2, RotateCcw, Trash2,
  ArrowLeft, MessageSquarePlus, Loader2, UserPlus, Sun, Moon, Filter, Users, ShieldCheck, History, Download, ThumbsUp,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICTS = [
  'Looks correct', 'Needs small edits', 'Wrong info', 'More info needed',
  'Outdated', 'Tone / clarity', 'Other',
];

const ACTION_LABEL = {
  assigned: 'assigned', delegated: 'delegated', edited: 'edited',
  commented: 'commented on', replied: 'replied on', resolved: 'resolved a comment on',
  published: 'published', unpublished: 'unpublished', deleted: 'moved to trash',
  restored: 'restored', purged: 'permanently deleted', verdict: 'set a verdict on',
};

const StatusPill = ({ s }) => {
  const map = {
    published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
    in_review: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
    draft: 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
    done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
    not_started: 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
  };
  const label = { published: 'Published', in_review: 'In review', draft: 'Draft', done: 'Done', not_started: 'Not started' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[s] || 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'}`}>{label[s] || s}</span>;
};

export default function ReviewConsole() {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [pid, setPid] = useState(null);
  const [config, setConfig] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [trash, setTrash] = useState([]);
  const [tab, setTab] = useState('overview');
  const [progress, setProgress] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [inbox, setInbox] = useState({ comments: [], unread: 0, open: 0, total: 0 });
  const [migratedOnly, setMigratedOnly] = useState(false);
  const [inboxStatus, setInboxStatus] = useState('all');
  const [inboxAuthor, setInboxAuthor] = useState('all');
  const [inboxSearch, setInboxSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState(null); // {slug,title,id}
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [verdict, setVerdict] = useState('');
  const [verdicts, setVerdicts] = useState([]);
  const [pubFilter, setPubFilter] = useState('all');
  const [mis, setMis] = useState(null);
  const [misSeed, setMisSeed] = useState(false);
  const [misPerson, setMisPerson] = useState('');
  const loadMis = useCallback(async () => {
    try { const r = await axios.get(`${API}/projects/${pid}/mis?include_seed=${misSeed}`); setMis(r.data); }
    catch (e) { toast.error('Failed to load MIS'); }
  }, [pid, misSeed]);
  useEffect(() => { if (tab === 'mis') loadMis(); }, [tab, loadMis]);
  const [fb, setFb] = useState(null);
  const loadFeedback = useCallback(async () => {
    try { const r = await axios.get(`${API}/projects/${pid}/feedback/summary`); setFb(r.data); }
    catch (e) { toast.error('Failed to load feedback'); }
  }, [pid]);
  useEffect(() => { if (tab === 'feedback' && pid) loadFeedback(); }, [tab, pid, loadFeedback]);
  const exportMisCsv = () => {
    if (!mis) return;
    const head = ['Reviewer', 'Assigned', 'Done', '% done', ...mis.verdict_labels, 'Done without verdict', 'Comments raised', 'Comments resolved', 'Overdue'];
    const rows = mis.reviewers.map(r => [r.email, r.assigned, r.done, r.pct_done, ...mis.verdict_labels.map(k => r.verdicts[k]), r.done_no_verdict, r.comments_made, r.comments_resolved, r.overdue]);
    const csv = [head, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'mis-reviewers.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const exportAltCsv = () => {
    if (!mis) return;
    const head = ['Page', 'Slug', 'Status', 'Images missing alt'];
    const rows = (mis.images_missing_alt || []).map(p => [p.title || '', p.slug, p.status || '', p.count]);
    const csv = [head, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'mis-images-missing-alt.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const exportInboxCsv = () => {
    const list = inbox.comments || [];
    const titleOfSlug = s => (documents.find(d => d.slug === s)?.title) || s;
    const head = ['Page', 'Slug', 'Author', 'Email', 'Status', 'Type', 'Selected text', 'Comment', 'Created', 'Resolved by'];
    const rows = list.map(c => [
      titleOfSlug(c.doc_slug), c.doc_slug, c.author_name || '', c.author_email || '',
      c.resolved ? 'Resolved' : 'Open', c.parent_id ? 'Reply' : 'Comment',
      c.anchor_text || '', c.body || '', c.created_at || '', c.resolved_by || '',
    ]);
    const csv = [head, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'review-comments.csv'; a.click(); URL.revokeObjectURL(url);
  };
  // assignment form
  const [aScopes, setAScopes] = useState([]);
  const [scopeQuery, setScopeQuery] = useState('');
  const [knownEmails, setKnownEmails] = useState([]);
  const [aEmail, setAEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewerFilter, setReviewerFilter] = useState('');
  const [owners, setOwners] = useState([]);
  const [scopeView, setScopeView] = useState('unassigned'); // unassigned | assigned | all
  // reviewer delegate form
  const [dScopes, setDScopes] = useState([]);
  const [dQuery, setDQuery] = useState('');
  const [dEmail, setDEmail] = useState('');
  const [activity, setActivity] = useState([]);
  const [activityPeople, setActivityPeople] = useState([]);
  const [pubSel, setPubSel] = useState(new Set());
  const [activityPerson, setActivityPerson] = useState('');
  const { isDark, toggleTheme } = useTheme();

  const isOwner = role?.is_owner;

  // Whose queue the reviewer-delegate panel operates on: a real reviewer delegates their own.
  const delegateFrom = (role?.email || '').toLowerCase();

  const myQueue = useMemo(() => {
    const s = new Set();
    if (!delegateFrom) return s;
    assignments.forEach((a) => {
      if ((a.assignee_email || '').toLowerCase() === delegateFrom) (a.slugs || []).forEach((x) => s.add(x));
    });
    return s;
  }, [assignments, delegateFrom]);

  const toggleDScope = (o) => {
    const key = `${o.type}:${o.id}`;
    const kids = (descendants[key] || []).filter((k) => myQueue.has(k.replace(/^page:/, '')));
    if (kids.length) {
      setDScopes((prev) => {
        const allIn = kids.every((k) => prev.includes(k));
        return allIn ? prev.filter((k) => !kids.includes(k)) : Array.from(new Set([...prev, ...kids]));
      });
    } else {
      setDScopes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }
  };

  const delegatePages = async () => {
    const selectedSlugs = dScopes.filter((k) => k.startsWith('page:')).map((k) => k.slice(5));
    if (!delegateFrom) { toast.error('Pick a reviewer first'); return; }
    if (selectedSlugs.length === 0 || !dEmail.trim()) { toast.error('Pick at least one page and enter an email'); return; }
    const to = dEmail.trim();
    if (to.toLowerCase() === delegateFrom) { toast.info('Already assigned to that email'); return; }
    if (!window.confirm(`Delegate ${selectedSlugs.length} page(s) from ${delegateFrom} to ${to}?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/projects/${pid}/assignments/delegate-pages`, {
        from_email: delegateFrom, to_email: to, slugs: selectedSlugs,
      });
      const asg = await axios.get(`${API}/projects/${pid}/assignments`);
      setAssignments(asg.data.assignments);
      setDScopes([]); setDEmail(''); setDQuery('');
      toast.success(`Delegated ${data.moved} page(s) to ${to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delegate'); } finally { setBusy(false); }
  };

  const byReviewer = useMemo(() => {
    const m = {};
    assignments.forEach(a => { const e = a.assignee_email || '—'; m[e] = (m[e] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [assignments]);

  // slug -> assignee email (first assignment that covers it)
  const assignedMap = useMemo(() => {
    const m = {};
    assignments.forEach(a => (a.slugs || []).forEach(s => { if (!m[s]) m[s] = a.assignee_email; }));
    return m;
  }, [assignments]);

  const docBySlug = useMemo(() => { const m = {}; documents.forEach(d => { m[d.slug] = d; }); return m; }, [documents]);
  // Slugs that have been soft-deleted (moved to Trash). Used to (a) tag them in the Publish
  // tab and (b) keep them out of the Assignments/Unassigned picker, regardless of any stale
  // documents/nav state on the client.
  const trashedSlugs = useMemo(() => new Set((trash || []).map(t => t.slug)), [trash]);
  const needsUpdate = (d) => !!d && d.status === 'published' && (d.content || '') !== (d.published_content || '');
  // Open (unresolved) comments per page — from the owner inbox feed.
  const openByPage = useMemo(() => {
    const m = {};
    (inbox.comments || []).forEach(c => { if (!c.resolved && c.doc_slug) m[c.doc_slug] = (m[c.doc_slug] || 0) + 1; });
    return m;
  }, [inbox]);
  // Images missing alt text per page — mirrors the backend publish gate.
  const countMissingAlt = (content) => {
    let c = content || '';
    c = c.replace(/```[\s\S]*?```/g, '').replace(/~~~[\s\S]*?~~~/g, '').replace(/`[^`]*`/g, '');
    let n = (c.match(/!\[\s*\]\([^)\s]+\)/g) || []).length;
    const tags = c.match(/<(?:Figure|img)\b[^>]*?\/?>/gi) || [];
    tags.forEach(t => { if (/\bsrc\s*=/i.test(t) && !/\balt\s*=/i.test(t)) n += 1; });
    return n;
  };
  const altByPage = useMemo(() => {
    const m = {};
    documents.forEach(d => { const n = countMissingAlt(d.content); if (n > 0) m[d.slug] = n; });
    return m;
  }, [documents]);
  // Page-level "Done": a "Looks correct" verdict (from the assigned reviewer or an owner) AND no open comments.
  const doneByPage = useMemo(() => {
    const ownerSet = new Set(owners || []);
    const looks = {};
    (verdicts || []).forEach(v => {
      if (v.verdict !== 'Looks correct') return;
      const re = (v.reviewer_email || '').toLowerCase();
      if (ownerSet.has(re) || re === (assignedMap[v.doc_slug] || '').toLowerCase()) looks[v.doc_slug] = true;
    });
    const m = {};
    Object.keys(looks).forEach(s => { if (!(openByPage[s] > 0)) m[s] = true; });
    return m;
  }, [verdicts, openByPage, assignedMap, owners]);
  const pageMatchesFilter = (d) => {
    if (pubFilter === 'in_review') return d.status !== 'published';
    if (pubFilter === 'published') return d.status === 'published';
    if (pubFilter === 'needs_update') return needsUpdate(d);
    return true;
  };

  const scopeOptions = useMemo(() => {
    const tabs = config?.navigation?.tabs || [];
    const opts = [];
    const seen = new Set();
    const titleOf = (slug) => (documents.find(d => d.slug === slug)?.title) || slug;
    const slugOf = (p) => (typeof p === 'string' ? p : p.page);
    tabs.forEach(t => {
      const tabId = t.id || t.label;
      opts.push({ type: 'tab', id: tabId, label: t.label, depth: 0 });
      const walk = (groups, depth) => {
        (groups || []).forEach(g => {
          opts.push({ type: 'group', id: `${tabId}::${g.group}`, label: g.group, depth });
          (g.pages || []).forEach(p => {
            const s = slugOf(p);
            if (!s) return;
            // Skip nav entries with no live document, or ones that have been moved to Trash:
            // deleted pages must never appear in the Assignments picker or as a live Publish row.
            if (!documents.some(d => d.slug === s) || trashedSlugs.has(s)) return;
            // De-duplicate by slug: a page can be linked in more than one nav location (e.g. the
            // web-to-mobile page appears under both Mobile Apps and the Web flow). The reviewer
            // should see it only once, so only the first occurrence becomes a scope option.
            if (seen.has(s)) return;
            seen.add(s);
            opts.push({ type: 'page', id: s, label: (typeof p === 'object' && p.title) || titleOf(s), depth: depth + 1 });
          });
          walk(g.groups, depth + 1);
        });
      };
      walk(t.groups, 1);
    });
    // pages that aren't linked anywhere in the nav tree
    documents.filter(d => !seen.has(d.slug) && !trashedSlugs.has(d.slug)).forEach(d => opts.push({ type: 'page', id: d.slug, label: d.title, depth: 0 }));
    return opts;
  }, [config, documents, trashedSlugs]);

  // Position of each page slug in the navigation (used to order the review list like the docs)
  const pageOrder = useMemo(() => {
    const m = {}; let i = 0;
    scopeOptions.forEach(o => { if (o.type === 'page' && !(o.id in m)) m[o.id] = i++; });
    return m;
  }, [scopeOptions]);

  // For each tab/section option key -> the list of descendant page keys (page:<slug>)
  const descendants = useMemo(() => {
    const map = {};
    const tabs = config?.navigation?.tabs || [];
    const slugOf = (p) => (typeof p === 'string' ? p : p.page);
    tabs.forEach(t => {
      const tabId = t.id || t.label;
      const tabPages = [];
      const collect = (gg) => {
        const acc = [];
        (gg.pages || []).forEach(p => { const s = slugOf(p); if (s) acc.push(`page:${s}`); });
        (gg.groups || []).forEach(sub => { acc.push(...collect(sub)); });
        map[`group:${tabId}::${gg.group}`] = acc;
        return acc;
      };
      (t.groups || []).forEach(g => { tabPages.push(...collect(g)); });
      map[`tab:${tabId}`] = Array.from(new Set(tabPages));
    });
    return map;
  }, [config]);

  const delegateOptions = useMemo(() => {
    if (myQueue.size === 0) return [];
    return scopeOptions.filter((o) => {
      if (o.type === 'page') return myQueue.has(o.id);
      const kids = descendants[`${o.type}:${o.id}`] || [];
      return kids.some((k) => myQueue.has(k.replace(/^page:/, '')));
    });
  }, [scopeOptions, descendants, myQueue]);

  // New-assignment picker: filter by assigned state + search
  const pageOptCount = useMemo(() => scopeOptions.filter(o => o.type === 'page').length, [scopeOptions]);
  const assignedPageCount = useMemo(() => scopeOptions.filter(o => o.type === 'page' && assignedMap[o.id]).length, [scopeOptions, assignedMap]);
  const visibleScopeOptions = useMemo(() => {
    const q = scopeQuery.toLowerCase();
    const pageVisible = (slug) => scopeView === 'all' ? true : scopeView === 'assigned' ? !!assignedMap[slug] : !assignedMap[slug];
    return scopeOptions.filter((o) => {
      if (!o.label.toLowerCase().includes(q)) return false;
      if (o.type === 'page') return pageVisible(o.id);
      const kids = descendants[`${o.type}:${o.id}`] || [];
      return kids.some((k) => pageVisible(k.replace(/^page:/, '')));
    });
  }, [scopeOptions, descendants, scopeView, scopeQuery, assignedMap]);

  // Keep Overview numbers fresh after assignment changes (no manual refresh needed)
  useEffect(() => {
    if (!pid || !isOwner) return;
    axios.get(`${API}/projects/${pid}/review/progress`).then(r => setProgress(r.data)).catch(() => {});
  }, [assignments, pid, isOwner]);

  // Load the global activity feed when the Activity tab (or its person filter) changes
  useEffect(() => {
    if (!pid || !isOwner || tab !== 'activity') return;
    const q = activityPerson ? `?person=${encodeURIComponent(activityPerson)}` : '';
    axios.get(`${API}/projects/${pid}/activity${q}`).then(r => {
      setActivity(r.data.activity || []);
      setActivityPeople(r.data.people || []);
    }).catch(() => {});
  }, [tab, activityPerson, pid, isOwner]);

  const load = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/roles/me`);
      setRole(me.data);
      if (!me.data.is_owner) setTab('assignments');
      const dp = await axios.get(`${API}/public/default-project`, { withCredentials: false });
      const projectId = dp.data.project.id;
      setPid(projectId);
      setConfig(dp.data.config);
      const docs = await axios.get(`${API}/projects/${projectId}/documents`);
      setDocuments(docs.data);
      const asg = await axios.get(`${API}/projects/${projectId}/assignments`);
      setAssignments(asg.data.assignments);
      try {
        const tr = await axios.get(`${API}/projects/${projectId}/trash`);
        setTrash(tr.data.trash || []);
      } catch (e) { /* trash best-effort */ }
      try {
        const vd = await axios.get(`${API}/projects/${projectId}/verdicts`);
        setVerdicts(vd.data.verdicts || []);
      } catch (e) { /* verdicts best-effort */ }
      try {
        if (me.data.is_owner) {
          const ke = await axios.get(`${API}/projects/${projectId}/known-emails`);
          setKnownEmails(ke.data.emails || []);
        }
      } catch (e) { /* suggestions are best-effort */ }
      if (me.data.is_owner) {
        const [pr, ib, ow] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/review/progress`),
          axios.get(`${API}/projects/${projectId}/review/inbox`),
          axios.get(`${API}/roles/owners`).catch(() => ({ data: { owners: [] } })),
        ]);
        setProgress(pr.data);
        setInbox(ib.data);
        setOwners((ow.data.owners || []).map(o => (o.email || '').toLowerCase()));
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load review console');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mark inbox comments read when the owner opens the Review Inbox (clears unread badge)
  useEffect(() => {
    if (tab !== 'inbox' || !isOwner || inbox.unread === 0) return;
    const me = role?.email;
    const unreadOnes = (inbox.comments || []).filter(c => !(c.read_by || []).includes(me) && c.author_email !== me);
    Promise.all(unreadOnes.map(c => axios.post(`${API}/projects/${pid}/comments/${c.id}/read`).catch(() => {})))
      .then(() => setInbox(prev => ({
        ...prev, unread: 0,
        comments: prev.comments.map(c => ({ ...c, read_by: [...(c.read_by || []), me] })),
      })));
  }, [tab, isOwner, pid, role, inbox.unread, inbox.comments]);

  // Refresh documents + nav + trash when the owner opens the Publish or Assignments tab, so a
  // page deleted elsewhere (editor/trash) is reflected immediately: kept out of the Assignments
  // picker and shown (tagged "Deleted") in the Publish tab without needing a manual reload.
  useEffect(() => {
    if (!isOwner || !pid || (tab !== 'publish' && tab !== 'assignments')) return;
    (async () => {
      try {
        const [docs, dp, tr] = await Promise.all([
          axios.get(`${API}/projects/${pid}/documents`),
          axios.get(`${API}/public/default-project`, { withCredentials: false }),
          axios.get(`${API}/projects/${pid}/trash`),
        ]);
        setDocuments(docs.data);
        setConfig(dp.data.config);
        setTrash(tr.data.trash || []);
      } catch (e) { /* best-effort refresh */ }
    })();
  }, [tab, isOwner, pid]);

  const openDoc = useCallback(async (doc) => {
    setActiveDoc(doc);
    setVerdict('');
    setComments([]);
    try {
      const [cm, vd] = await Promise.all([
        axios.get(`${API}/projects/${pid}/comments`, { params: { doc_slug: doc.slug } }),
        axios.get(`${API}/projects/${pid}/verdicts`, { params: { doc_slug: doc.slug } }),
      ]);
      setComments(cm.data.comments);
      const mine = (vd.data.verdicts || []).find(v => v.reviewer_email === role?.email);
      setVerdict(mine?.verdict || '');
    } catch (e) {
      toast.error('Failed to load comments');
    }
  }, [pid, role]);

  const addComment = async () => {
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/projects/${pid}/comments`, { doc_slug: activeDoc.slug, body: newComment.trim() });
      setComments(prev => [...prev, data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (e) { toast.error('Failed to add comment'); } finally { setBusy(false); }
  };

  const saveVerdict = async (v) => {
    setVerdict(v);
    try {
      await axios.post(`${API}/projects/${pid}/verdicts`, { doc_slug: activeDoc.slug, verdict: v });
      toast.success('Verdict saved');
    } catch (e) { toast.error('Failed to save verdict'); }
  };

  const resolveComment = async (c, resolve) => {
    try {
      await axios.post(`${API}/projects/${pid}/comments/${c.id}/${resolve ? 'resolve' : 'reopen'}`);
      setComments(prev => prev.map(x => x.id === c.id ? { ...x, resolved: resolve } : x));
      setInbox(prev => ({ ...prev, comments: prev.comments.map(x => x.id === c.id ? { ...x, resolved: resolve } : x) }));
    } catch (e) { toast.error('Action failed'); }
  };

  const createAssignment = async () => {
    if (aScopes.length === 0 || !aEmail.trim()) { toast.error('Pick at least one item and enter an email'); return; }
    setBusy(true);
    try {
      const results = await Promise.allSettled(aScopes.map((key) => {
        const opt = scopeOptions.find((o) => `${o.type}:${o.id}` === key);
        return axios.post(`${API}/projects/${pid}/assignments`, {
          scope_type: opt.type, scope_id: opt.id,
          scope_label: opt.label,
          assignee_email: aEmail.trim(),
        });
      }));
      const email = aEmail.trim();
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      const asg = await axios.get(`${API}/projects/${pid}/assignments`);
      setAssignments(asg.data.assignments);
      if (failed === 0) {
        setAEmail(''); setAScopes([]); setScopeQuery('');
        toast.success(`Assigned ${ok} item(s) to ${email}`);
      } else {
        toast.error(`${ok} assigned, ${failed} failed — try the failed ones again`);
      }
    } catch (e) { toast.error('Failed to assign'); } finally { setBusy(false); }
  };

  const toggleScope = (key) => {
    const kids = descendants[key];
    if (kids && kids.length) {
      // Tab / section: cascade-select all its pages
      setAScopes((prev) => {
        const allIn = kids.every((k) => prev.includes(k));
        if (allIn) return prev.filter((k) => !kids.includes(k));
        return Array.from(new Set([...prev, ...kids]));
      });
    } else {
      setAScopes((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    }
  };

  const setAssignmentStatus = async (a, status) => {
    try {
      await axios.put(`${API}/projects/${pid}/assignments/${a.id}`, { status });
      setAssignments(prev => prev.map(x => x.id === a.id ? { ...x, status } : x));
      toast.success(`Marked ${status.replace('_', ' ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const delegate = async (a) => {
    const email = window.prompt(`Currently assigned to ${a.assignee_email}.\nReassign this review to which email?`);
    if (!email) return;
    if (email.trim().toLowerCase() === (a.assignee_email || '').toLowerCase()) {
      toast.info('Already assigned to that email'); return;
    }
    if (!window.confirm(`This was assigned to ${a.assignee_email}.\nAre you sure you want to reassign it to ${email.trim()}?`)) return;
    try {
      await axios.post(`${API}/projects/${pid}/assignments/${a.id}/delegate`, { email: email.trim() });
      const asg = await axios.get(`${API}/projects/${pid}/assignments`);
      setAssignments(asg.data.assignments);
      toast.success(`Reassigned to ${email.trim()}`);
    } catch (e) { toast.error('Failed to reassign'); }
  };

  const promote = async (email) => {
    if (!window.confirm(`Make ${email} an Owner? Owners can assign, publish, and manage roles.`)) return;
    try {
      await axios.post(`${API}/roles/promote`, { email });
      const ow = await axios.get(`${API}/roles/owners`);
      setOwners((ow.data.owners || []).map(o => (o.email || '').toLowerCase()));
      toast.success(`${email} is now an Owner`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to promote'); }
  };

  const bulkDelegate = async (fromEmail, count) => {
    const to = window.prompt(`Delegate all ${count} review(s) assigned to ${fromEmail} to which email?`);
    if (!to) return;
    const t = to.trim();
    if (t.toLowerCase() === (fromEmail || '').toLowerCase()) { toast.info('Already assigned to that email'); return; }
    if (!window.confirm(`Reassign all ${count} review(s) from ${fromEmail} to ${t}?`)) return;
    try {
      const { data } = await axios.post(`${API}/projects/${pid}/assignments/delegate-bulk`, { from_email: fromEmail, to_email: t });
      const asg = await axios.get(`${API}/projects/${pid}/assignments`);
      setAssignments(asg.data.assignments);
      toast.success(`Reassigned ${data.reassigned} review(s) to ${t}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delegate'); }
  };

  const togglePub = (slug) => setPubSel(prev => { const n = new Set(prev); n.has(slug) ? n.delete(slug) : n.add(slug); return n; });
  const bulkPublish = async (slugs, publish) => {
    const targets = [...new Set(slugs)].map(s => docBySlug[s]).filter(Boolean)
      .filter(d => publish ? (d.status !== 'published' || needsUpdate(d)) : d.status === 'published');
    if (!targets.length) { toast(`Nothing to ${publish ? 'publish' : 'take down'}`); return; }
    await Promise.allSettled(targets.map(d => axios.post(`${API}/projects/${pid}/documents/${d.id}/${publish ? 'publish' : 'unpublish'}`)));
    setDocuments(prev => prev.map(d => targets.find(t => t.id === d.id)
      ? { ...d, status: publish ? 'published' : 'in_review', published_content: publish ? d.content : null, published_title: publish ? d.title : null }
      : d));
    setPubSel(new Set());
    toast.success(`${targets.length} page(s) ${publish ? 'published' : 'taken down'}`);
  };

  const publishDoc = async (doc, publish) => {
    try {
      await axios.post(`${API}/projects/${pid}/documents/${doc.id}/${publish ? 'publish' : 'unpublish'}`);
      setDocuments(prev => prev.map(d => d.id === doc.id
        ? { ...d, status: publish ? 'published' : 'in_review', published_content: publish ? d.content : null, published_title: publish ? d.title : null }
        : d));
      toast.success(publish ? 'Published' : 'Taken down');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const restorePage = async (item) => {
    if (!window.confirm(`Restore "${item.title || item.slug}" from Trash? It will return to the navigation and show up under Unassigned.`)) return;
    try {
      await axios.post(`${API}/projects/${pid}/documents/${item.id}/restore-page`);
      const [docs, dp, tr, asg] = await Promise.all([
        axios.get(`${API}/projects/${pid}/documents`),
        axios.get(`${API}/public/default-project`, { withCredentials: false }),
        axios.get(`${API}/projects/${pid}/trash`),
        axios.get(`${API}/projects/${pid}/assignments`),
      ]);
      setDocuments(docs.data);
      setConfig(dp.data.config);
      setTrash(tr.data.trash || []);
      setAssignments(asg.data.assignments || []);
      toast.success('Page restored — now under Unassigned');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
  };

  const deletePage = async (item) => {
    if (!window.confirm(`Move "${item.title || item.slug}" to Trash? It will be removed from the navigation and any assignments. You can restore it from the Deleted pages section for 90 days.`)) return;
    try {
      await axios.delete(`${API}/projects/${pid}/documents/${item.id}`);
      const [docs, dp, tr, asg] = await Promise.all([
        axios.get(`${API}/projects/${pid}/documents`),
        axios.get(`${API}/public/default-project`, { withCredentials: false }),
        axios.get(`${API}/projects/${pid}/trash`),
        axios.get(`${API}/projects/${pid}/assignments`),
      ]);
      setDocuments(docs.data);
      setConfig(dp.data.config);
      setTrash(tr.data.trash || []);
      setAssignments(asg.data.assignments || []);
      toast.success('Page moved to Trash — see Deleted pages below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  const TabBtn = ({ id, icon: Icon, label, badge }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`review-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === id ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
    >
      <Icon className="w-4 h-4" /> {label}
      {badge > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white" data-testid={`${id}-badge`}>{badge}</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100" data-testid="review-console">
      <header className="h-14 px-6 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/dashboard')} className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md" data-testid="review-back"><ArrowLeft className="w-4 h-4" /></button>
          <h1 className="font-semibold">Review Console</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400 font-medium" data-testid="review-role">{isOwner ? 'Owner' : 'Reviewer'}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleTheme} className="p-1.5 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400" data-testid="theme-toggle" aria-label="Toggle theme">
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">{role?.email}</span>
        </div>
      </header>

      <div className="px-6 py-3 flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <TabBtn id="mis" icon={BarChart3} label="MIS" />
        <TabBtn id="feedback" icon={ThumbsUp} label="Feedback" />
        {isOwner && <TabBtn id="overview" icon={BarChart3} label="Overview" />}
        <TabBtn id="assignments" icon={ClipboardList} label={isOwner ? 'Assignments' : 'My Reviews'} />
        {isOwner && <TabBtn id="inbox" icon={Inbox} label="Review Inbox" badge={inbox.unread} />}
        {isOwner && <TabBtn id="publish" icon={Send} label="Publish" />}
        {isOwner && <TabBtn id="team" icon={ShieldCheck} label="Team" />}
        {isOwner && <TabBtn id="activity" icon={History} label="Activity" />}
      </div>

      <main className="p-6 max-w-5xl mx-auto">
        {/* OVERVIEW */}
        {tab === 'overview' && isOwner && (
          <div className="grid grid-cols-2 gap-4" data-testid="review-overview">
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold mb-3">Documents</h3>
              {Object.entries(progress?.docs_by_status || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1.5"><StatusPill s={k} /><span className="font-semibold">{v}</span></div>
              ))}
            </div>
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold mb-3">Assignments</h3>
              <p className="text-sm text-zinc-500 mb-2">{progress?.total_assignments || 0} total</p>
              {Object.entries(progress?.assignments_by_status || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1.5"><StatusPill s={k} /><span className="font-semibold">{v}</span></div>
              ))}
              <div className="mt-3 flex items-center justify-between border-t border-zinc-200 dark:border-zinc-800 pt-3">
                <span className="text-sm text-zinc-500">Open comments</span><span className="font-semibold">{inbox.open}</span>
              </div>
            </div>
          </div>
        )}

        {/* ASSIGNMENTS */}
        {tab === 'assignments' && (
          <div data-testid="review-assignments">
            {isOwner && (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5 mb-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4" /> New assignment</h3>
                <div className="grid gap-3">
                  <div>
                    <label className="text-xs text-zinc-500">Assign these (pick one or many)</label>
                    <input
                      value={scopeQuery}
                      onChange={(e) => setScopeQuery(e.target.value)}
                      placeholder="Filter tabs / sections / pages…"
                      data-testid="assign-scope-search"
                      className="mt-1 w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md px-3 py-2 text-sm"
                    />
                    <div className="mt-2 flex items-center gap-1 text-xs" data-testid="assign-scope-view">
                      {[['unassigned', `Unassigned (${pageOptCount - assignedPageCount})`], ['assigned', `Assigned (${assignedPageCount})`], ['all', 'All']].map(([v, lbl]) => (
                        <button key={v} onClick={() => setScopeView(v)} data-testid={`scope-view-${v}`} className={`px-2 py-1 rounded-md border ${scopeView === v ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}>{lbl}</button>
                      ))}
                    </div>
                    <div className="mt-2 max-h-56 overflow-y-auto border border-zinc-200 dark:border-zinc-800 rounded-md divide-y divide-zinc-100 dark:divide-zinc-800" data-testid="assign-scope-list">
                      {visibleScopeOptions.length === 0 && <p className="px-3 py-3 text-sm text-zinc-500 dark:text-zinc-400" data-testid="assign-scope-empty">No {scopeView === 'unassigned' ? 'unassigned' : scopeView} pages.</p>}
                      {visibleScopeOptions
                        .slice(0, 300)
                        .map((o) => {
                          const key = `${o.type}:${o.id}`;
                          const kids = descendants[key];
                          const checked = kids && kids.length ? kids.every((k) => aScopes.includes(k)) : aScopes.includes(key);
                          const some = kids && kids.length ? kids.some((k) => aScopes.includes(k)) : false;
                          const assignee = o.type === 'page' ? assignedMap[o.id] : null;
                          return (
                            <label key={key} style={{ paddingLeft: 12 + o.depth * 18 }} className={`flex items-center gap-2 pr-3 py-1.5 text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 ${o.type !== 'page' ? 'font-medium' : ''}`} data-testid={`assign-option-${o.type}-${o.id}`}>
                              <input type="checkbox" checked={checked} ref={(el) => { if (el) el.indeterminate = some && !checked; }} onChange={() => toggleScope(key)} className="accent-zinc-900 dark:accent-white" />
                              <span className={`text-[10px] uppercase tracking-wide px-1 py-0.5 rounded ${o.type === 'tab' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400' : o.type === 'group' ? 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300' : 'bg-transparent text-zinc-400'}`}>{o.type === 'tab' ? 'Tab' : o.type === 'group' ? 'Sec' : 'Pg'}</span>
                              <span className={o.type === 'tab' ? 'text-indigo-700 dark:text-indigo-400' : o.type === 'group' ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'}>{o.label}</span>
                              {assignee && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 whitespace-nowrap" data-testid={`assigned-badge-${o.id}`}>→ {assignee}</span>}
                            </label>
                          );
                        })}
                    </div>
                    {aScopes.length > 0 && <p className="text-xs text-zinc-500 mt-1" data-testid="assign-selected-count">{aScopes.length} item(s) selected</p>}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      value={aEmail}
                      onChange={(e) => setAEmail(e.target.value)}
                      placeholder="reviewer@emergent.sh"
                      list="known-emails"
                      data-testid="assign-email"
                      className="border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md px-3 py-2 text-sm min-w-[240px]"
                    />
                    <datalist id="known-emails">
                      {knownEmails.map((em) => <option key={em} value={em} />)}
                    </datalist>
                    <button onClick={createAssignment} disabled={busy} data-testid="assign-submit" className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm font-medium rounded-md disabled:opacity-50">
                      Assign {aScopes.length > 0 ? `(${aScopes.length})` : ''}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delegate — owner sees per-reviewer mass delegate; reviewer picks pages (all / several / one) */}
            {isOwner ? (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5 mb-5" data-testid="bulk-delegate-panel">
                <h3 className="font-semibold mb-1 flex items-center gap-2"><Users className="w-4 h-4" /> Delegate all reviews (per reviewer)</h3>
                <p className="text-xs text-zinc-500 mb-3">Reassign everything currently on one reviewer's plate to someone else in one step.</p>
                {byReviewer.length === 0 ? (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="bulk-delegate-empty">No reviewers have assignments yet.</p>
                ) : (
                  <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {byReviewer.map(([email, count]) => (
                      <div key={email} className="flex items-center justify-between py-2 text-sm" data-testid={`bulk-delegate-row-${email}`}>
                        <span className="text-zinc-700 dark:text-zinc-200">{email} <span className="text-zinc-400">· {count} review(s)</span></span>
                        <button onClick={() => bulkDelegate(email, count)} className="text-xs px-2.5 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800" data-testid={`bulk-delegate-btn-${email}`}>Delegate all →</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5 mb-5" data-testid="reviewer-delegate-panel">
                <h3 className="font-semibold mb-1 flex items-center gap-2"><Users className="w-4 h-4" /> Delegate pages</h3>
                <p className="text-xs text-zinc-500 mb-3">Hand off some or all of your assigned pages to another reviewer — pick all, several, or just one.</p>
                {!delegateFrom ? (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="reviewer-delegate-hint">Type a reviewer's email in the “Show reviews for email…” box below to load their pages.</p>
                ) : delegateOptions.length === 0 ? (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="reviewer-delegate-empty">No pages assigned to {delegateFrom}.</p>
                ) : (
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-zinc-500">Choose pages to delegate</label>
                      <div className="flex gap-3">
                        <button onClick={() => setDScopes(delegateOptions.filter((o) => o.type === 'page').map((o) => `page:${o.id}`))} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="delegate-select-all">Select all</button>
                        <button onClick={() => setDScopes([])} className="text-xs text-zinc-500 hover:underline" data-testid="delegate-clear">Clear</button>
                      </div>
                    </div>
                    <input value={dQuery} onChange={(e) => setDQuery(e.target.value)} placeholder="Filter your pages…" data-testid="delegate-scope-search" className="w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md px-3 py-2 text-sm" />
                    <div className="max-h-56 overflow-y-auto border border-zinc-200 dark:border-zinc-800 rounded-md divide-y divide-zinc-100 dark:divide-zinc-800" data-testid="delegate-scope-list">
                      {delegateOptions
                        .filter((o) => o.label.toLowerCase().includes(dQuery.toLowerCase()))
                        .map((o) => {
                          const key = `${o.type}:${o.id}`;
                          const kids = (descendants[key] || []).filter((k) => myQueue.has(k.replace(/^page:/, '')));
                          const checked = kids.length ? kids.every((k) => dScopes.includes(k)) : dScopes.includes(key);
                          const some = kids.length ? kids.some((k) => dScopes.includes(k)) : false;
                          return (
                            <label key={key} style={{ paddingLeft: 12 + o.depth * 18 }} className={`flex items-center gap-2 pr-3 py-1.5 text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 ${o.type !== 'page' ? 'font-medium' : ''}`} data-testid={`delegate-option-${o.type}-${o.id}`}>
                              <input type="checkbox" checked={checked} ref={(el) => { if (el) el.indeterminate = some && !checked; }} onChange={() => toggleDScope(o)} className="accent-zinc-900 dark:accent-white" />
                              <span className={`text-[10px] uppercase tracking-wide px-1 py-0.5 rounded ${o.type === 'tab' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400' : o.type === 'group' ? 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300' : 'bg-transparent text-zinc-400'}`}>{o.type === 'tab' ? 'Tab' : o.type === 'group' ? 'Sec' : 'Pg'}</span>
                              <span className={o.type === 'tab' ? 'text-indigo-700 dark:text-indigo-400' : o.type === 'group' ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'}>{o.label}</span>
                            </label>
                          );
                        })}
                    </div>
                    {dScopes.length > 0 && <p className="text-xs text-zinc-500" data-testid="delegate-selected-count">{dScopes.filter((k) => k.startsWith('page:')).length} page(s) selected</p>}
                    <div className="flex flex-wrap gap-2 items-center">
                      <input value={dEmail} onChange={(e) => setDEmail(e.target.value)} placeholder="reviewer@emergent.sh" list="known-emails-delegate" data-testid="delegate-email" className="border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md px-3 py-2 text-sm min-w-[240px]" />
                      <datalist id="known-emails-delegate">{knownEmails.map((em) => <option key={em} value={em} />)}</datalist>
                      <button onClick={delegatePages} disabled={busy} data-testid="delegate-submit" className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm font-medium rounded-md disabled:opacity-50">
                        Delegate {dScopes.filter((k) => k.startsWith('page:')).length > 0 ? `(${dScopes.filter((k) => k.startsWith('page:')).length})` : ''}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isOwner && (
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 max-w-xs">
                <Filter className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  value={reviewerFilter}
                  onChange={(e) => setReviewerFilter(e.target.value)}
                  placeholder="Show reviews for email…"
                  data-testid="assign-reviewer-filter"
                  className="w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md pl-8 pr-3 py-1.5 text-sm"
                />
              </div>
              {reviewerFilter && <button onClick={() => setReviewerFilter('')} className="text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">Clear</button>}
            </div>
            )}
            <div className="space-y-2">
              {(() => {
                const shown = assignments
                  .filter(a => !reviewerFilter.trim() || (a.assignee_email || '').toLowerCase().includes(reviewerFilter.trim().toLowerCase()))
                  .slice()
                  .sort((x, y) => {
                    const ord = (a) => Math.min(...((a.slugs || []).map(s => (s in pageOrder ? pageOrder[s] : 1e9))), 1e9);
                    return ord(x) - ord(y);
                  });
                if (shown.length === 0) return <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="assign-empty">{assignments.length === 0 ? 'No assignments yet.' : 'No reviews match that email.'}</p>;
                return shown.map(a => (
                <div key={a.id} className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 flex items-center justify-between" data-testid={`assignment-${a.id}`}>
                  <div className="flex-1">
                    {(a.slugs || []).length === 1 ? (
                      <button onClick={() => navigate(`/review/${a.slugs[0]}?reviewer=${encodeURIComponent(a.assignee_email || '')}`)} className="font-medium text-left text-indigo-700 dark:text-indigo-400 hover:underline" data-testid={`review-page-${a.slugs[0]}`}>
                        {(documents.find(x => x.slug === a.slugs[0])?.title) || a.scope_label}
                      </button>
                    ) : (
                      <div className="font-medium">{a.scope_label} <span className="text-xs text-zinc-400">({a.scope_type})</span></div>
                    )}
                    <div className="text-xs text-zinc-500">{a.assignee_email} · {a.slugs?.length || 0} page(s){(a.assigned_by_name || a.assigned_by) ? ` · assigned by ${a.assigned_by_name || a.assigned_by}` : ''}{a.delegated_from ? ` · delegated from ${a.delegated_from}` : ''}</div>
                    {(a.slugs || []).length > 1 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(a.slugs || []).map(s => {
                        const doc = documents.find(x => x.slug === s);
                        return (
                          <button key={s} onClick={() => navigate(`/review/${s}?reviewer=${encodeURIComponent(a.assignee_email || '')}`)} className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-zinc-600 dark:text-zinc-300" data-testid={`review-page-${s}`}>
                            {doc?.title || s}
                          </button>
                        );
                      })}
                    </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const total = (a.slugs || []).length;
                      const dn = (a.slugs || []).filter(s => doneByPage[s]).length;
                      const all = total > 0 && dn === total;
                      return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${all ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'}`} title="A page is Done when it has a 'Looks correct' verdict and no open comments" data-testid={`assignment-done-count-${a.id}`}>{all ? <><CheckCircle2 className="w-3 h-3" /> Done</> : `${dn}/${total} done`}</span>;
                    })()}
                    {a.status === 'done'
                      ? <button onClick={() => setAssignmentStatus(a, 'in_review')} className="text-xs px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800" data-testid={`assignment-reopen-${a.id}`}>Reopen</button>
                      : <button onClick={() => setAssignmentStatus(a, 'done')} className="text-xs px-2 py-1 rounded-md border border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10" data-testid={`assignment-done-${a.id}`}>Mark done</button>}
                    <button onClick={() => delegate(a)} className="text-xs px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800" data-testid={`assignment-delegate-${a.id}`}>Delegate</button>
                    {isOwner && <button data-testid={`assignment-delete-btn-${a.id}`} onClick={async () => { await axios.delete(`${API}/projects/${pid}/assignments/${a.id}`); setAssignments(p => p.filter(x => x.id !== a.id)); }} className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                ));
              })()}
            </div>
          </div>
        )}
        {tab === 'inbox' && isOwner && (
          <div className="space-y-2" data-testid="review-inbox">
            {(() => {
              const isMigrated = c => (c.body || '').startsWith('[migrated from review sheet]') || c.author_email === 'docs-bot@emergent.sh';
              const migratedCount = inbox.comments.filter(isMigrated).length;
              const authors = Array.from(new Map(inbox.comments.map(c => [c.author_email, c.author_name || c.author_email])).entries());
              const q = inboxSearch.trim().toLowerCase();
              const shown = inbox.comments.filter(c =>
                (!migratedOnly || isMigrated(c)) &&
                (inboxStatus === 'all' || (inboxStatus === 'resolved' ? c.resolved : !c.resolved)) &&
                (inboxAuthor === 'all' || c.author_email === inboxAuthor) &&
                (!q || (c.body || '').toLowerCase().includes(q) || (c.anchor_text || '').toLowerCase().includes(q)));
              const openCount = inbox.comments.filter(c => !c.resolved).length;
              const resolvedCount = inbox.comments.length - openCount;
              return (<>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <input
                    type="text" value={inboxSearch} onChange={e => setInboxSearch(e.target.value)}
                    placeholder="Search comments & selected text…" data-testid="inbox-search"
                    className="text-xs border border-zinc-200 dark:border-zinc-800 rounded-md px-2 py-1 bg-transparent dark:text-zinc-200 w-56 placeholder:text-zinc-400" />
                  <div className="inline-flex rounded-md border border-zinc-200 dark:border-zinc-800 overflow-hidden text-xs" data-testid="inbox-filter-status">
                    {[['all', `All (${inbox.comments.length})`], ['open', `Open (${openCount})`], ['resolved', `Resolved (${resolvedCount})`]].map(([v, label]) => (
                      <button key={v} onClick={() => setInboxStatus(v)} data-testid={`inbox-status-${v}`}
                        className={`px-3 py-1 transition-colors ${inboxStatus === v ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900' : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}>{label}</button>
                    ))}
                  </div>
                  <select value={inboxAuthor} onChange={e => setInboxAuthor(e.target.value)} data-testid="inbox-filter-author"
                    className="text-xs border border-zinc-200 dark:border-zinc-800 rounded-md px-2 py-1 bg-transparent dark:text-zinc-200">
                    <option value="all">All authors</option>
                    {authors.map(([email, name]) => <option key={email} value={email}>{name}</option>)}
                  </select>
                  {migratedCount > 0 && (
                    <button
                      onClick={() => setMigratedOnly(v => !v)}
                      data-testid="inbox-filter-migrated"
                      className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border transition-colors ${migratedOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30'}`}>
                      <Send className="w-3 h-3" /> Migrated from review sheet ({migratedCount}){migratedOnly ? ' · clear' : ''}
                    </button>
                  )}
                  {(inboxStatus !== 'all' || inboxAuthor !== 'all' || migratedOnly || inboxSearch) && (
                    <button onClick={() => { setInboxStatus('all'); setInboxAuthor('all'); setMigratedOnly(false); setInboxSearch(''); }} data-testid="inbox-filter-clear"
                      className="text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 underline">Clear filters</button>
                  )}
                  <button onClick={exportInboxCsv} data-testid="inbox-export-csv"
                    className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                    <Download className="w-3 h-3" /> Export all comments ({inbox.comments.length})
                  </button>
                  <span className="text-xs text-zinc-400" data-testid="inbox-filter-count">{shown.length} shown</span>
                </div>
                {shown.length === 0 && <p className="text-sm text-zinc-500">No comments match these filters.</p>}
                {shown.map(c => (
                  <div key={c.id} className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4" data-testid={`inbox-comment-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium flex items-center gap-2"><button onClick={() => navigate(`/review/${c.doc_slug}?focus=${encodeURIComponent(c.anchor_text || '')}`)} className="hover:underline" data-testid={`inbox-open-${c.id}`}>{c.author_name || c.author_email} <span className="text-xs text-zinc-400">on {c.doc_slug}</span></button>{isMigrated(c) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">migrated</span>}</span>
                      {c.resolved
                        ? <button onClick={() => resolveComment(c, false)} className="text-xs flex items-center gap-1 text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200"><RotateCcw className="w-3 h-3" /> Reopen</button>
                        : <button onClick={() => resolveComment(c, true)} className="text-xs flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800" data-testid={`resolve-${c.id}`}><CheckCircle2 className="w-3 h-3" /> Resolve</button>}
                    </div>
                    {c.anchor_text && <div className="text-xs italic text-zinc-500 dark:text-zinc-400 border-l-2 border-zinc-300 dark:border-zinc-700 pl-2 mb-1">“{c.anchor_text}”</div>}
                    <p className="text-sm text-zinc-700 dark:text-zinc-200">{c.body}</p>
                    {c.resolved && <span className="text-[10px] text-emerald-600 font-medium">Resolved</span>}
                  </div>
                ))}
              </>);
            })()}
          </div>
        )}

        {/* MIS — read-only management dashboard, open to all signed-in users */}
        {tab === 'mis' && (
          <div data-testid="review-mis" className="space-y-6">
            {!mis ? <p className="text-sm text-zinc-500">Loading…</p> : (<>
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <button onClick={loadMis} className="px-3 py-1.5 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900" data-testid="mis-refresh">Refresh</button>
                <span className="text-zinc-500 dark:text-zinc-400">Data as of {new Date(mis.generated_at).toLocaleString()}</span>
                <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={misSeed} onChange={e => setMisSeed(e.target.checked)} data-testid="mis-seed-toggle" /> include seed/test</label>
                <select value={misPerson} onChange={e => setMisPerson(e.target.value)} className="border border-zinc-300 dark:border-zinc-700 rounded px-2 py-1 bg-transparent" data-testid="mis-person">
                  <option value="">All reviewers</option>
                  {mis.reviewers.map(r => <option key={r.email} value={r.email}>{r.email}</option>)}
                </select>
                <button onClick={exportMisCsv} className="ml-auto px-3 py-1.5 rounded-md border border-zinc-300 dark:border-zinc-700" data-testid="mis-csv">Export CSV</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="mis-funnel">
                {[['Total pages', mis.funnel.total_pages], ['Assigned', mis.funnel.assigned], ['Done', mis.funnel.done], ['Published', mis.funnel.published], ['No verdict', mis.funnel.no_verdict]].map(([l, n]) => (
                  <div key={l} className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3"><div className="text-2xl font-semibold">{n}</div><div className="text-xs text-zinc-500">{l}</div></div>
                ))}
              </div>
              <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-lg" data-testid="mis-reviewer-table">
                <table className="w-full text-xs"><thead className="bg-zinc-50 dark:bg-zinc-900/50 text-zinc-500"><tr>
                  {[['Reviewer',''], ['Assigned',''], ['Done',''], ['% done',''], ['Looks correct',''], ['Other verdicts',''], ['Done w/o verdict',''], ['Raised','Comments this reviewer raised'], ['Resolved','Of those raised, how many are now resolved'], ['Overdue','']].map(([h, t]) => <th key={h} title={t} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}
                </tr></thead><tbody>
                  {mis.reviewers.filter(r => !misPerson || r.email === misPerson).map(r => (
                    <tr key={r.email} className="border-t border-zinc-100 dark:border-zinc-800" data-testid={`mis-row-${r.email}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{r.email}</td><td className="px-3 py-2">{r.assigned}</td><td className="px-3 py-2">{r.done}</td><td className="px-3 py-2">{r.pct_done}%</td>
                      <td className="px-3 py-2">{r.verdicts['Looks correct']}</td>
                      <td className="px-3 py-2">{Object.entries(r.verdicts).filter(([k]) => k !== 'Looks correct').reduce((a, [, n]) => a + n, 0)}</td>
                      <td className="px-3 py-2">{r.done_no_verdict}</td><td className="px-3 py-2">{r.comments_made}</td><td className="px-3 py-2">{r.comments_resolved}</td><td className="px-3 py-2">{r.overdue}</td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
              {mis.throughput.length > 0 && (
                <div data-testid="mis-throughput">
                  <h3 className="font-semibold text-sm mb-2">Throughput <span className="text-xs text-zinc-500 font-normal">(pages reaching "Looks correct" per day)</span></h3>
                  <div className="flex items-end gap-1 h-24">
                    {mis.throughput.map(t => {
                      const max = Math.max(...mis.throughput.map(x => x.count), 1);
                      return <div key={t.day} className="flex flex-col items-center justify-end flex-1" title={`${t.day}: ${t.count}`}>
                        <div className="w-full bg-indigo-500/70 dark:bg-indigo-400/70 rounded-t" style={{ height: `${(t.count / max) * 100}%` }} />
                        <span className="text-[9px] text-zinc-400 mt-1 rotate-0">{t.day.slice(5)}</span>
                      </div>;
                    })}
                  </div>
                </div>
              )}
              <div data-testid="mis-comments-health">
                <h3 className="font-semibold text-sm mb-2">Comments health <span className="text-xs text-zinc-500 font-normal">({mis.comments_health.filter(p => p.hot).length} pages with ≥3 open)</span></h3>
                <div className="flex flex-wrap gap-1">
                  {mis.comments_health.slice(0, 60).map(p => (
                    <button key={p.slug} onClick={() => navigate(`/review/${p.slug}`)} className={`text-[11px] px-2 py-1 rounded border ${p.hot ? 'border-rose-300 text-rose-600 dark:border-rose-500/40 dark:text-rose-400' : 'border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300'}`} title={p.title} data-testid={`mis-ch-${p.slug}`}>{(p.title || p.slug).slice(0, 24)} · {p.open}/{p.open + p.resolved}</button>
                  ))}
                </div>
              </div>
              {/* Bulk alt audit — pages that cannot be published until images get alt text */}
              <div data-testid="mis-images-missing-alt">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-sm">Images missing alt text <span className="text-xs text-zinc-500 font-normal">({(mis.images_missing_alt || []).length} page{(mis.images_missing_alt || []).length === 1 ? '' : 's'} — blocks publishing)</span></h3>
                  {(mis.images_missing_alt || []).length > 0 && (
                    <button onClick={exportAltCsv} className="ml-auto text-[11px] px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800" data-testid="mis-alt-export">Export CSV</button>
                  )}
                </div>
                {(mis.images_missing_alt || []).length === 0 ? (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Every page's images have alt text.</p>
                ) : (
                  <div className="rounded-lg border border-rose-200 dark:border-rose-500/30 divide-y divide-rose-100 dark:divide-rose-500/20 max-h-64 overflow-y-auto" data-testid="mis-alt-list">
                    {mis.images_missing_alt.map(p => (
                      <button key={p.slug} onClick={() => navigate(`/admin/editor/${pid}/${(docBySlug[p.slug] || {}).id || ''}`)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-rose-50 dark:hover:bg-rose-500/10" title="Open in editor to add alt text" data-testid={`mis-alt-${p.slug}`}>
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400 font-medium flex-shrink-0">{p.count} missing</span>
                        <span className="text-sm text-zinc-700 dark:text-zinc-200 truncate flex-1">{p.title || p.slug}</span>
                        <StatusPill s={p.status || 'in_review'} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="mis-exceptions">
                {[['Done without verdict', mis.exceptions.done_no_verdict], ['Wrong info verdict', mis.exceptions.wrong_info_unedited], ['Looks-correct with open bot comment', mis.exceptions.looks_correct_open_nr], ['Duplicate titles', mis.exceptions.duplicate_titles]].map(([l, arr]) => (
                  <div key={l} className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
                    <div className="text-sm font-medium mb-1">{l} <span className="text-xs text-zinc-500">({arr.length})</span></div>
                    <div className="space-y-0.5 max-h-40 overflow-y-auto">{arr.slice(0, 25).map((x, i) => <div key={i} className="text-xs text-zinc-600 dark:text-zinc-300 truncate">{x.title || x.slug || (x.slugs && x.slugs.join(', '))}</div>)}</div>
                  </div>
                ))}
              </div>
            </>)}
          </div>
        )}

        {/* FEEDBACK — what readers said on the public docs ("Was this page helpful?") */}
        {tab === 'feedback' && (
          <div data-testid="review-feedback" className="space-y-6">
            {!fb ? <p className="text-sm text-zinc-500">Loading…</p> : (<>
              <div className="flex items-center gap-3 text-xs">
                <button onClick={loadFeedback} className="px-3 py-1.5 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900" data-testid="feedback-refresh">Refresh</button>
                <span className="text-zinc-500 dark:text-zinc-400">Reader feedback from the public docs, worst-rated pages first</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="feedback-totals">
                {[['Responses', fb.total_responses], ['Helpful', fb.helpful], ['Not helpful', fb.unhelpful], ['Helpful rate', `${fb.helpful_pct}%`]].map(([l, n]) => (
                  <div key={l} className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3"><div className="text-2xl font-semibold">{n}</div><div className="text-xs text-zinc-500">{l}</div></div>
                ))}
              </div>
              {fb.top_reasons.length > 0 && (
                <div data-testid="feedback-reasons">
                  <h3 className="font-semibold text-sm mb-2">Why readers answered the way they did</h3>
                  <div className="space-y-1.5">
                    {fb.top_reasons.map(([label, n]) => {
                      const good = label.startsWith('+ ');
                      const max = Math.max(...fb.top_reasons.map((x) => x[1]), 1);
                      return (
                        <div key={label} className="flex items-center gap-3 text-xs">
                          <span className={`w-24 flex-shrink-0 px-1.5 py-0.5 rounded text-center font-medium ${good ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400'}`}>{good ? 'Helpful' : 'Improve'}</span>
                          <span className="flex-1 truncate text-zinc-700 dark:text-zinc-200">{label.slice(2)}</span>
                          <div className="w-32 h-2 rounded bg-zinc-100 dark:bg-zinc-800"><div className={`h-2 rounded ${good ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${(n / max) * 100}%` }} /></div>
                          <span className="w-6 text-right font-semibold">{n}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {fb.pages.length === 0 ? (
                <p className="text-sm text-zinc-500" data-testid="feedback-empty">No reader feedback yet. It will appear here as readers answer "Was this page helpful?" on the public docs.</p>
              ) : (
                <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-800 rounded-lg" data-testid="feedback-table">
                  <table className="w-full text-xs"><thead className="bg-zinc-50 dark:bg-zinc-900/50 text-zinc-500"><tr>
                    {['Page', 'Helpful', 'Not helpful', 'Score', 'Top reason', 'Comments'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}
                  </tr></thead><tbody>
                    {fb.pages.map((p) => {
                      const top = Object.entries(p.reasons || {}).sort((a, b) => b[1] - a[1])[0];
                      return (
                        <tr key={p.slug} className="border-t border-zinc-100 dark:border-zinc-800 align-top" data-testid={`feedback-row-${p.slug}`}>
                          <td className="px-3 py-2"><a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">{p.title}</a></td>
                          <td className="px-3 py-2">{p.up}</td>
                          <td className="px-3 py-2">{p.down}</td>
                          <td className="px-3 py-2 whitespace-nowrap"><span className={`px-1.5 py-0.5 rounded font-medium ${p.score >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' : p.score >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400'}`}>{p.score}%</span> <span className="text-zinc-400">of {p.total}</span></td>
                          <td className="px-3 py-2 text-zinc-600 dark:text-zinc-300">{top ? `${top[0]} (${top[1]})` : '—'}</td>
                          <td className="px-3 py-2">
                            {(p.comments || []).length === 0 ? <span className="text-zinc-400">—</span> : (
                              <details><summary className="cursor-pointer text-zinc-600 dark:text-zinc-300">{p.comments.length} comment{p.comments.length === 1 ? '' : 's'}</summary>
                                <ul className="mt-1 space-y-1">{p.comments.map((c, i) => <li key={i} className="text-zinc-600 dark:text-zinc-300"><span className={c.helpful ? 'text-emerald-600' : 'text-rose-600'}>{c.helpful ? '▲' : '▼'}</span> {c.comment}</li>)}</ul>
                              </details>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody></table>
                </div>
              )}
            </>)}
          </div>
        )}

        {/* PUBLISH */}
        {tab === 'publish' && isOwner && (
          <div data-testid="review-publish">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <button onClick={() => bulkPublish([...pubSel], true)} disabled={pubSel.size === 0} className="text-xs px-3 py-1.5 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 disabled:opacity-40" data-testid="bulk-publish-selected">Publish selected ({pubSel.size})</button>
              <button onClick={() => bulkPublish([...pubSel], false)} disabled={pubSel.size === 0} className="text-xs px-3 py-1.5 rounded-md border border-rose-300 dark:border-rose-500/40 text-rose-600 dark:text-rose-400 disabled:opacity-40" data-testid="bulk-takedown-selected">Take down selected</button>
              {pubSel.size > 0 && <button onClick={() => setPubSel(new Set())} className="text-xs px-2 py-1.5 rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800">Clear</button>}
              <span className="text-[11px] text-zinc-500 dark:text-zinc-400 ml-auto flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> = "Looks correct" verdict, no open comments</span>
            </div>
            <div className="flex items-center gap-1 mb-3 flex-wrap" data-testid="publish-filter">
              {[['all', 'All'], ['in_review', 'In review'], ['published', 'Published'], ['needs_update', 'Needs update']].map(([k, lbl]) => (
                <button key={k} onClick={() => setPubFilter(k)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${pubFilter === k ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid={`publish-filter-${k}`}>{lbl}</button>
              ))}
            </div>
            <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg divide-y divide-zinc-100 dark:divide-zinc-800 overflow-hidden">
              {scopeOptions.map((o) => {
                if (o.type !== 'page') {
                  const kids = (descendants[`${o.type}:${o.id}`] || []).map(k => k.replace(/^page:/, ''));
                  const visibleKids = kids.filter(s => docBySlug[s] && pageMatchesFilter(docBySlug[s]));
                  if (visibleKids.length === 0) return null;
                  const pubKids = visibleKids.filter(s => docBySlug[s].status !== 'published' || needsUpdate(docBySlug[s]));
                  return (
                    <div key={`${o.type}:${o.id}`} style={{ paddingLeft: 12 + o.depth * 18 }} className="flex items-center gap-2 pr-3 py-2 bg-zinc-50/70 dark:bg-zinc-900/50" data-testid={`publish-group-${o.id}`}>
                      <span className={`text-[10px] uppercase tracking-wide px-1 py-0.5 rounded ${o.type === 'tab' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400' : 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'}`}>{o.type === 'tab' ? 'Tab' : 'Sec'}</span>
                      <span className={`text-sm font-medium ${o.type === 'tab' ? 'text-indigo-700 dark:text-indigo-400' : 'text-zinc-700 dark:text-zinc-300'}`}>{o.label}</span>
                      {pubKids.length > 0 && (
                        <button onClick={() => bulkPublish(pubKids, true)} className="ml-auto text-[11px] px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800" data-testid={`publish-all-${o.id}`}>Publish all ({pubKids.length})</button>
                      )}
                    </div>
                  );
                }
                const d = docBySlug[o.id];
                if (!d || !pageMatchesFilter(d)) return null;
                const done = doneByPage[o.id];
                const openCt = openByPage[o.id] || 0;
                const altCt = altByPage[o.id] || 0;
                const blocked = openCt > 0 || altCt > 0;
                const blockTitle = openCt > 0 ? 'Resolve open comments first' : (altCt > 0 ? 'Add alt text to all images first' : '');
                const nu = needsUpdate(d);
                return (
                  <div key={`page:${o.id}`} style={{ paddingLeft: 12 + o.depth * 18 }} className="flex items-center gap-2 pr-3 py-2 bg-white dark:bg-zinc-900" data-testid={`publish-row-${d.id}`}>
                    <input type="checkbox" checked={pubSel.has(o.id)} onChange={() => togglePub(o.id)} className="accent-zinc-900 dark:accent-white" data-testid={`publish-check-${d.id}`} />
                    <button onClick={() => navigate(`/review/${d.slug}`)} className="text-sm text-left hover:underline truncate" data-testid={`publish-title-${d.id}`}>{d.title}</button>
                    <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                      {done && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 font-medium flex items-center gap-1" data-testid={`reviewed-done-${d.id}`}><CheckCircle2 className="w-3 h-3" /> Reviewed</span>}
                      {blocked && openCt > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400 font-medium" title={`${openCt} open comment(s) block publishing`} data-testid={`blocked-comments-${d.id}`}>{openCt} open</span>}
                      {altCt > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400 font-medium" title={`${altCt} image(s) missing alt text block publishing`} data-testid={`blocked-alt-${d.id}`}>{altCt} missing alt</span>}
                      {nu && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 font-medium" title="This page's draft differs from the live published copy" data-testid={`needs-update-${d.id}`}>Unpublished changes</span>}
                      {d.reviewer_edited_by && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 font-medium" title={`Edited by ${d.reviewer_edited_by}`} data-testid={`edited-by-reviewer-${d.id}`}>Edited by reviewer</span>}
                      <StatusPill s={d.status || 'in_review'} />
                      <button onClick={() => navigate(`/admin/editor/${pid}/${d.id}`)} className="text-xs px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800" data-testid={`open-editor-${d.id}`}>Open in editor</button>
                      {d.status === 'published'
                        ? <>
                            {nu && <button onClick={() => publishDoc(d, true)} disabled={blocked} title={blocked ? blockTitle : 'Push the current draft live'} className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white disabled:opacity-40" data-testid={`republish-${d.id}`}>Republish</button>}
                            <button onClick={() => publishDoc(d, false)} title="Returns the page to In review and removes it from the public site" className="text-xs px-2 py-1 rounded-md border border-rose-300 dark:border-rose-500/40 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10" data-testid={`takedown-${d.id}`}>Take down</button>
                          </>
                        : <button onClick={() => publishDoc(d, true)} disabled={blocked} title={blocked ? blockTitle : ''} className="text-xs px-2 py-1 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 disabled:opacity-40" data-testid={`publish-${d.id}`}>Publish</button>}
                      <button onClick={() => deletePage(d)} title="Move this page to Trash (soft delete — restorable for 90 days)" className="text-xs px-2 py-1 rounded-md border border-rose-300 dark:border-rose-500/40 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center gap-1" data-testid={`delete-page-${d.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {trash.length > 0 && (
              <div className="mt-6" data-testid="publish-deleted-section">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">Deleted pages <span className="text-xs font-normal text-zinc-500">({trash.length} in Trash · kept for 90 days)</span></h3>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">These pages were removed and can't be published. Restore one to put it back in the navigation (it will reappear under Unassigned).</p>
                <div className="border border-rose-200 dark:border-rose-500/30 rounded-lg divide-y divide-rose-100 dark:divide-rose-500/20 overflow-hidden">
                  {trash.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 pr-3 py-2 pl-3 bg-rose-50/50 dark:bg-rose-500/5" data-testid={`publish-deleted-row-${t.id}`}>
                      <span className="text-sm text-zinc-500 dark:text-zinc-400 line-through truncate" data-testid={`deleted-title-${t.id}`}>{t.title || t.slug}</span>
                      <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400 font-medium" data-testid={`deleted-badge-${t.id}`}>Deleted</span>
                        {t.deleted_by && <span className="text-[11px] text-zinc-500 dark:text-zinc-400 hidden sm:inline" title={`Deleted by ${t.deleted_by_name || t.deleted_by}`}>by {t.deleted_by_name || t.deleted_by}{typeof t.days_left === 'number' ? ` · ${t.days_left}d left` : ''}</span>}
                        <button onClick={() => restorePage(t)} className="text-xs px-2 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-1" data-testid={`restore-deleted-${t.id}`}><RotateCcw className="w-3 h-3" /> Restore</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'team' && isOwner && (
          <div className="max-w-2xl" data-testid="review-team">
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5 mb-5">
              <h3 className="font-semibold mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Owners</h3>
              <p className="text-xs text-zinc-500 mb-3">Owners can assign reviews, publish pages, and promote others.</p>
              <div className="flex flex-wrap gap-2" data-testid="team-owners">
                {owners.length === 0 ? <span className="text-sm text-zinc-500">No owners yet.</span> : owners.map(em => (
                  <span key={em} className="text-sm px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400 font-medium" data-testid={`team-owner-${em}`}>{em}</span>
                ))}
              </div>
            </div>
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold mb-1 flex items-center gap-2"><Users className="w-4 h-4" /> Reviewers &amp; people</h3>
              <p className="text-xs text-zinc-500 mb-3">Everyone the workspace knows. Promote a trusted reviewer to Owner in one click.</p>
              {knownEmails.filter(em => !owners.includes(em)).length === 0 ? (
                <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="team-reviewers-empty">No other people yet — they'll appear here once they log in or get an assignment.</p>
              ) : (
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {knownEmails.filter(em => !owners.includes(em)).map(em => (
                    <div key={em} className="flex items-center justify-between py-2 text-sm" data-testid={`team-person-${em}`}>
                      <span className="text-zinc-700 dark:text-zinc-200">{em}</span>
                      <button onClick={() => promote(em)} className="text-xs px-2.5 py-1 rounded-md border border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-1.5" data-testid={`make-owner-${em}`}><ShieldCheck className="w-3.5 h-3.5" /> Make owner</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {tab === 'activity' && isOwner && (
          <div data-testid="review-activity">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-zinc-500" />
              <h3 className="font-semibold">Activity</h3>
              <select value={activityPerson} onChange={e => setActivityPerson(e.target.value)} data-testid="activity-person-filter" className="ml-auto text-sm border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md px-2 py-1">
                <option value="">Everyone</option>
                {activityPeople.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            {activity.length === 0 ? (
              <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="activity-empty">No activity yet.</p>
            ) : (
              <div className="relative pl-4 border-l border-zinc-200 dark:border-zinc-800 space-y-4" data-testid="activity-feed">
                {activity.map(ev => (
                  <div key={ev.id} className="relative" data-testid={`activity-${ev.id}`}>
                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-indigo-500" />
                    <div className="text-sm text-zinc-700 dark:text-zinc-200">
                      <span className="font-medium">{ev.actor_name || ev.actor_email}</span>{' '}
                      {ACTION_LABEL[ev.action] || ev.action}
                      {(ev.doc_title || ev.doc_slug) ? <> <button onClick={() => (ev.doc_slug ? navigate(`/review/${ev.doc_slug}`) : null)} className="text-indigo-600 dark:text-indigo-400 hover:underline">{ev.doc_title || ev.doc_slug}</button></> : null}
                      {ev.meta?.assignee ? <span className="text-zinc-500"> → {ev.meta.assignee}</span> : null}
                      {ev.meta?.to ? <span className="text-zinc-500"> ({ev.meta.from} → {ev.meta.to})</span> : null}
                      {ev.meta?.verdict ? <span className="text-zinc-500"> — “{ev.meta.verdict}”</span> : null}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">{new Date(ev.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      {activeDoc && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setActiveDoc(null)}>
          <div className="w-[440px] h-full bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 shadow-xl p-5 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="review-drawer">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{activeDoc.title}</h3>
              <button onClick={() => navigate(`/review/${activeDoc.slug}`)} className="text-xs px-2 py-1 rounded-md border border-indigo-300 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10" data-testid="open-inline-review">Open full page ↗</button>
            </div>
            <label className="text-xs text-zinc-500">Verdict</label>
            <div className="flex flex-wrap gap-1.5 my-2">
              {VERDICTS.map(v => (
                <button key={v} onClick={() => saveVerdict(v)} className={`text-xs px-2 py-1 rounded-full border ${verdict === v ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 border-zinc-900 dark:border-white' : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid={`verdict-${v.replace(/\W+/g, '-')}`}>{v}</button>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-xs text-zinc-500">Comments</label>
              <div className="space-y-2 my-2">
                {comments.map(c => (
                  <div key={c.id} className="text-sm border border-zinc-200 dark:border-zinc-800 rounded-md p-2">
                    <div className="text-xs text-zinc-400">{c.author_name || c.author_email}{c.resolved ? ' · resolved' : ''}</div>
                    <p>{c.body}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-zinc-400">No comments yet.</p>}
              </div>
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Leave a comment…" data-testid="drawer-comment-input" className="w-full border border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-md p-2 text-sm" rows={3} />
              <button onClick={addComment} disabled={busy} className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-sm rounded-md disabled:opacity-50" data-testid="drawer-comment-submit"><MessageSquarePlus className="w-4 h-4" /> Add comment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
